import {API_URL, APP_NAME} from '@/config';
import {UserFacingError} from '@/domain/useCases/exceptionHandling';
import {post} from '../repository/jsonHttp';
import {decodeJwt, isTokenExpired} from './jwt';

const refreshToken = async () => {
  const access = localStorage.getItem(`${APP_NAME}.accessToken`) || undefined;
  const refresh = localStorage.getItem(`${APP_NAME}.refreshToken`) || undefined;
  
  if (refresh === undefined) {
    return {
      access: undefined,
      refresh: undefined,
    };
  }

  const accessToken = access ? decodeJwt(access) : undefined;
  const refreshToken = refresh ? decodeJwt(refresh) : undefined;
  if (accessToken && !isTokenExpired(accessToken, 30)) {
    return {access, refresh};
  } else if (!isTokenExpired(refreshToken, 30)) {
    try {
      const response = await post<JwtRefreshRequest, JwtCreateResponse>({
        path: 'auth/jwt/refresh/',
        baseUrl: '/api/v1/',
        body: {refresh}
      });

      localStorage.setItem(`${APP_NAME}.accessToken`, response.access);
      localStorage.setItem(`${APP_NAME}.refreshToken`, refresh);

      return {
        access: response.access,
        refresh: refresh,
      };
    } catch (error) {
      console.error(error);
    }
  }

  return {
    access: undefined,
    refresh: undefined,
  };
};

const login = async (username: string, password: string): Promise<void> => {
  try {
    const response = await post<JwtCreateRequest, JwtCreateResponse>({
      path: 'auth/jwt/create/',
      baseUrl: API_URL,
      body: {username, password}
    });
    
    localStorage.setItem(`${APP_NAME}.accessToken`, response.access);
    localStorage.setItem(`${APP_NAME}.refreshToken`, response.refresh);

  } catch (error) {
    console.error(error);
    throw new LoginError('authentication.loginError');
  }
};

const logout = () => {
  localStorage.removeItem(`${APP_NAME}.accessToken`);
  localStorage.removeItem(`${APP_NAME}.refreshToken`);
};

const isAuthenticated = async (): Promise<boolean> => {
  const {access, refresh} = await refreshToken();
  return access !== undefined && refresh !== undefined;
};

export class LoginError extends UserFacingError {
  loginErrorKey: string;

  constructor(loginErrorKey: string) {
    super([loginErrorKey]);
    this.loginErrorKey = loginErrorKey;
  }
}

export default {
  login,
  logout,
  refreshToken,
  isAuthenticated,
};

export type JwtCreateRequest = {
  username: string,
  password: string,
}

export type JwtRefreshRequest = {
  refresh: string,
}

export type JwtCreateResponse = {
  access: string,
  refresh: string,
}
